/**
 * Checklist de découverte (P0).
 *
 * Deux fonctionnalités différenciantes de DooDates ne sont trouvées par
 * personne. Mesuré en production le 15/09/2026, sur les 25 comptes réels
 * (hors comptes de test) : **3 groupes réutilisables** au total, chez deux
 * comptes de l'entourage du fondateur, et **zéro série récurrente** — les
 * quatre séries qui existent appartiennent toutes à des comptes de test.
 *
 * Ce n'est donc pas un problème d'adoption, c'est un problème de découverte :
 * il n'existe aujourd'hui aucun endroit dans le produit qui dise que ces deux
 * choses existent. La checklist est cet endroit. Elle s'affiche à tous les
 * niveaux de plan, pas seulement aux nouveaux inscrits — le signal de
 * dogfooding montre que l'ancienneté du compte n'y change rien.
 *
 * Ce module ne contient que la dérivation, sans React ni Supabase, pour
 * rester testable et réutilisable (le même calcul servira au tableau de bord
 * admin quand il répondra à la question P0).
 */

/** Identifiants stables : servent de clés i18n et d'identifiants analytics. */
export type DiscoveryStepId = "group" | "recurring";

export interface DiscoveryStep {
  id: DiscoveryStepId;
  /** L'utilisateur l'a déjà fait au moins une fois. */
  done: boolean;
  /**
   * Son plan ne lui permet pas de le faire aujourd'hui. L'étape reste
   * affichée — c'est précisément ce qu'on veut lui faire découvrir — mais
   * elle mène aux tarifs plutôt qu'à la fonctionnalité.
   */
  locked: boolean;
}

export interface DiscoveryCounts {
  /** `null` tant que le décompte n'est pas connu (chargement, erreur). */
  groups: number | null;
  recurringSeries: number | null;
}

export interface DiscoveryOptions {
  /** Résultat de `canCreateGroup(profile)`. */
  canCreateGroup: boolean;
  /** Résultat de `isPaidPlanType(profile?.plan_type)` — les séries aussi. */
  canCreateRecurring: boolean;
}

/**
 * Renvoie les étapes à afficher, ou `null` quand il n'y a rien à afficher.
 *
 * `null` couvre deux cas volontairement confondus : les décomptes ne sont pas
 * encore connus, et tout est déjà fait. Dans les deux cas l'appelant ne rend
 * rien — pas de squelette de chargement pour une carte secondaire, et pas de
 * carte vide qui félicite.
 */
export function deriveDiscoverySteps(
  counts: DiscoveryCounts,
  options: DiscoveryOptions,
): DiscoveryStep[] | null {
  if (counts.groups === null || counts.recurringSeries === null) return null;

  const steps: DiscoveryStep[] = [
    {
      id: "group",
      done: counts.groups > 0,
      locked: !options.canCreateGroup,
    },
    {
      id: "recurring",
      done: counts.recurringSeries > 0,
      locked: !options.canCreateRecurring,
    },
  ];

  return steps.every((step) => step.done) ? null : steps;
}
